import { useRef, useState } from "react";
import { logDevError } from "../logging/AgTechLogger";

export const agtechDelay = async (waitFor: number) => await new Promise(res => setTimeout(res, waitFor));

export const isDev = () => process.env.NODE_ENV === 'development';

export const getProgressColor = (progress: number) => {
    let red = 255 - (progress > 50 ? (progress - 50) * 2 : 0);
    let green = 50 + (4 * progress);
    let blue = 20;

    return `rgb(${red}, ${green}, ${blue})`;
}

export const executeAfter = async <TResult, >(props: {
    action: () => Promise<TResult>,
    delay: number,
    onComplete?: (result: TResult) => Promise<void>
}): Promise<TResult> => {
    return new Promise(async (resolve) => {
        setTimeout(() => {
            props.action().then(async (result) => {
                if (props.onComplete) {
                    await props.onComplete(result);
                };

                resolve(result);
            });
        }, props.delay);
    });
}

export const executeWithMinimumDelay = async <TResult, >(props: {
    action: () => Promise<TResult>,
    minimumDelay: number,
    onComplete?: (result: TResult) => Promise<void>
}): Promise<TResult> => {
    const startTime = Date.now();

    const result = await props.action();

    const elapsedTime = Date.now() - startTime;

    if (elapsedTime < props.minimumDelay) {
        await agtechDelay(props.minimumDelay - elapsedTime);
    }

    if (props.onComplete) {
        await props.onComplete(result);
    }

    return result;
}

export const useAgTechState = <TState, >(
    defaultState: TState,
    onStateUpdated?: (updatedState: TState) => void
): [React.MutableRefObject<TState>, (func: (currentState: TState) => TState) => TState] => {
    let [state, setState] = useState<TState>(defaultState);
    let stateRef = useRef<TState>(state);

    const updateState = (func: (currState: TState) => TState) => {
        let updatedState = func(stateRef.current);

        stateRef.current = updatedState;
        setState(stateRef.current);

        if (onStateUpdated) {
            onStateUpdated(stateRef.current);
        }

        return stateRef.current;
    }

    return [stateRef, updateState];
}

export const useAgTechSafeState = <TState, >(defaultState: TState, stateUpdateCallback?: (updatedState: TState) => void): [React.MutableRefObject<TState>, (state: TState) => void] => {
    let [state, setState] = useState<TState>(defaultState);
    let stateRef = useRef<TState>(state);

    const updateState = (updatedState: TState) => {
        stateRef.current = updatedState;
        setState(stateRef.current);

        if (stateUpdateCallback) {
            stateUpdateCallback(updatedState);
        }

        return stateRef.current;
    }

    return [stateRef, updateState];
}

export const useAgTechSafeStateFunc = <TState, >(defaultState: () => TState, stateUpdateCallback?: (updatedState: TState) => void): [React.MutableRefObject<TState>, (state: TState) => void] => {
    let [state, setState] = useState<TState>(defaultState);
    let stateRef = useRef<TState>(state);

    const updateState = (updatedState: TState) => {
        stateRef.current = updatedState;
        setState(stateRef.current);

        if (stateUpdateCallback) {
            stateUpdateCallback(updatedState);
        }

        return stateRef.current;
    }

    return [stateRef, updateState];
}

export const Index = <TType, >(array: TType[], key: (item: TType) => string | number): { [key: string | number]: TType } => {
    return array ? array.reduce((index, item) => {
        index[key(item)] = item;
        return index;
    }, {} as { [key: string | number]: TType }) : {}
};

export const IndexLength = (index: {}) => index ? Object.keys(index).length : 0;

export type AgTechIndex<TType extends { id: string | number }> = { [key: string | number]: TType };

export const DoSafely = (action: () => void, onError?: (error: any) => void) => {
    try {
        action();
    } catch (error) {
        if (onError) {
            onError(error);
        }
        else {
            logDevError(error);
        }
    }
}